import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    AttributeEditor,
    ColumnLayout,
    CustomDetailEvent,
    DatePicker,
    FormField,
    Input,
    Select,
    TimeInput,
} from '@amzn/awsui-components-react-v2';
import { SpaceBetween } from '@amzn/awsui-components-react-v3';
import {
    FormSectionMode,
    LocationFormFieldText,
} from '../../../common/constants/forms';
import { ActivityAudienceItemData } from '../../../common/interfaces/businessDataItem/activityAudienceItem';
import { ActivityModalityItemData } from '../../../common/interfaces/businessDataItem/activityModalityItem';
import { ActivityTypeItemData } from '../../../common/interfaces/businessDataItem/activityTypeItem';
import SelectActivityType from '../Activity/FormSections/CommonFormFields/SelectActivityType';
import { CourseItemData } from '../../../common/interfaces/businessDataItem/courseItem';
import { DeliveryLanguageItemData } from '../../../common/interfaces/businessDataItem/deliveryLanguageItem';
import { LocationItemData } from '../../../common/interfaces/businessDataItem/locationItem';
import { PartnerInitiativeItemData } from '../../../common/interfaces/businessDataItem/partnerInitiativeItem';
import { ProgramTypeItemData } from '../../../common/interfaces/businessDataItem/programTypeItem';
import { UserProfileData } from '../../../common/interfaces/userProfile';
import {
    getActivityAudiencesList,
    resetActivityAudiencesSlice,
} from '../../../common/store/slices/activityAudiencesSlice';
import { useFilteredAudiences } from '../../../common/hooks/useFilteredAudiences';
import {
    getActivityModalitiesList,
    resetActivityModalitiesSlice,
    selectError as selectActivityModalityListError,
    selectAllActiveActivityModalities,
    selectIsLoaded as selectIsActivityModalityListLoaded,
    selectIsLoading as selectIsActivityModalityListLoading,
} from '../../../common/store/slices/activityModalitiesSlice';
import {
    getActivityStatusesList,
    resetActivityStatusesSlice,
    selectIsLoaded as selectIsActivityStatusListLoaded,
    selectIsLoading as selectIsActivityStatusListLoading,
} from '../../../common/store/slices/activityStatusesSlice';
import {
    getActivityTypesList,
    resetActivityTypesSlice,
    selectAllActiveActivityTypes,
    selectError as selectActivityTypeListError,
    selectIsLoaded as selectIsActivityTypeListLoaded,
    selectIsLoading as selectIsActivityTypeListLoading,
} from '../../../common/store/slices/activityTypesSlice';
import {
    getCoursesList,
    resetCoursesSlice,
    selectAllActiveCourses,
    selectError as selectCourseListError,
    selectSearchText as selectCourseListSearchText,
    selectIsLoaded as selectIsCourseListLoaded,
    selectIsLoading as selectIsCourseListLoading,
    setSearchText as setCourseListSearchText,
} from '../../../common/store/slices/coursesSlice';
import {
    getDeliveryLanguagesList,
    resetDeliveryLanguagesSlice,
    selectAllActiveDeliveryLanguages,
    selectError as selectDeliveryLanguageListError,
    selectIsLoaded as selectIsDeliveryLanguageLoaded,
    selectIsLoading as selectIsDeliveryLanguageLoading,
} from '../../../common/store/slices/deliveryLanguagesSlice';
import { resetInstructorRoleTypesSlice } from '../../../common/store/slices/instructorRoleTypesSlice';
import {
    getLocationsList,
    resetLocationsSlice,
    resetPartialLocationsSlice,
    selectAllActiveLocations,
    selectIsLoaded as selectIsLocationListLoaded,
    selectIsLoading as selectIsLocationListLoading,
    selectError as selectLocationListError,
    selectSearchText as selectLocationListSearchText,
    setRegion as setLocationListRegion,
    setSearchText as setLocationListSearchText,
} from '../../../common/store/slices/locationsSlice';
import {
    getPartnerInitiativesList,
    resetPartnerInitiativesSlice,
    selectAllActivePartnerInitiatives,
    selectIsLoaded as selectIsPartnerInitiativeListLoaded,
    selectIsLoading as selectIsPartnerInitiativeListLoading,
    selectError as selectPartnerInitiativeListError,
} from '../../../common/store/slices/partnerInitiativesSlice';
import {
    getUserList,
    resetUserListSlice,
    selectIsLoaded as selectIsUserListLoaded,
    selectIsLoading as selectIsUserListLoading,
    selectUserList,
    selectError as selectUserListError,
    selectSearchText as selectUserListSearchText,
    setSearchText as setUserListSearchText,
} from '../../../common/store/slices/userListSlice';
import { selectUser } from '../../../common/store/slices/userSlice';
import handlePreSelectedValue from '../../../common/utils/handlePreSelectedValue';
import { getCityString } from '../../../imt/components/Instructor/FormSections/BasicInfoFormSection';
import {
    FORM_ERROR_SELECTOR,
    getOptionsAndLookupForSelectInput,
    getStatusType,
} from '../../../imt/components/Instructor/FormSections/FormSections.common';
import { CreateActivityData } from '../../interfaces/activity';
import {
    meridiemFieldOptionLookup,
    meridiemFieldOptions,
    MeridiemFieldValue,
} from '../../interfaces/meridiem';
import {
    ActivityFormSectionProps,
    createEmptySessionAttributeEditorItem,
    SessionAttributeEditorItem,
} from '../Activity/Common/Common';
import '../Activity/FormSections/ActivityFormSection.scss';
import { EmailTokenInput } from '../Activity/Details/CustomersTab/EmailTokenInput';

export const getActivityCityString = (cityData: {
    delivery_city: string | null;
    delivery_state?: string;
    delivery_country: string;
}) => {
    const {
        delivery_city: city,
        delivery_country: country,
        delivery_state: state,
    } = cityData;
    return getCityString({
        city,
        country,
        state,
    });
};

// Session constants
const ONE_DAY = 8; // one day is 8 hours
const DAY_START_TIME = 9; // default start time for session is 9:00 AM

const getPaddedDateValue = (val: number) => val.toString().padStart(2, '0');

export const getDefaultSessionsForCourse = (
    startDateString: string,
    course: string,
    courseList: Array<CourseItemData>,
): Array<SessionAttributeEditorItem> => {
    const sessions = [] as Array<SessionAttributeEditorItem>;
    const [year, month, day] = startDateString
        .split('-')
        .map((val) => parseInt(val, 10));
    // create a local date object so that we are not impacted by Timezone offsets and manually set the year, month, and date values for date calculations
    let startDate = new Date();
    startDate.setFullYear(year, month - 1, day);

    let courseDays = 1;

    if (course) {
        courseDays = parseFloat(
            courseList.find((courseItem) => courseItem.course === course)
                ?.course_days ?? '1',
        );
    }

    const fractionalDays = courseDays % 1;
    let fullDays = courseDays - fractionalDays;

    while (fullDays > 0) {
        const endTime = DAY_START_TIME + ONE_DAY - 12;
        sessions.push({
            dateString: `${startDate.getFullYear()}-${getPaddedDateValue(
                startDate.getMonth() + 1, // increment month by 1 as to not mess up session calculations in getDeliverySession
            )}-${getPaddedDateValue(startDate.getDate())}`, // pad left with zeroes if single digit to not mess up session calculations in getDeliverySession
            startTime: `${getPaddedDateValue(DAY_START_TIME)}:00`, // pad left with zeroes if single digit to not mess up session calculations in getDeliverySession
            endTime: `${getPaddedDateValue(endTime)}:00`, // pad left with zeroes if single digit to not mess up session calculations in getDeliverySession
            startTimeMeridiemFieldOption: MeridiemFieldValue.Am,
            endTimeMeridiemFieldOption: MeridiemFieldValue.Pm,
        });

        // increment start date
        startDate.setDate(startDate.getDate() + 1);

        fullDays--;
    }

    if (fractionalDays) {
        const sessionHours = Math.floor(fractionalDays * ONE_DAY);
        const sessionMinutes = Math.floor(
            ((fractionalDays * ONE_DAY) % 1) * 60,
        );

        const endTime =
            DAY_START_TIME + sessionHours > 12
                ? DAY_START_TIME + sessionHours - 12
                : DAY_START_TIME + sessionHours;

        sessions.push({
            dateString: `${startDate.getFullYear()}-${getPaddedDateValue(
                startDate.getMonth() + 1, // increment month by 1 as to not mess up session calculations in getDeliverySession
            )}-${getPaddedDateValue(startDate.getDate())}`, // pad left with zeroes if single digit to not mess up session calculations in getDeliverySession
            startTime: `${getPaddedDateValue(DAY_START_TIME)}:00`, // pad left with zeroes if single digit to not mess up session calculations in getDeliverySession
            endTime: `${getPaddedDateValue(endTime)}:${getPaddedDateValue(sessionMinutes)}`, // pad left with zeroes if single digit to not mess up session calculations in getDeliverySession
            startTimeMeridiemFieldOption: MeridiemFieldValue.Am,
            endTimeMeridiemFieldOption:
                sessionHours >= 4
                    ? MeridiemFieldValue.Pm
                    : MeridiemFieldValue.Am,
        });
    }

    return sessions;
};

const OpporotunityDetailsFormSection = ({
    formValues,
    errors,
    handleFieldEvent,
    mode,
    handleSessionItemEvent,
    sessionAttributeEditorItems,
    controlArrayErrors,
    handleShortNameChange,
    handleCourseDaysChange,
    handleCustomersChange,
    handleInvoicesChange,
    handleCountryCodeChange,
}: ActivityFormSectionProps<Omit<CreateActivityData, 'delivery_sessions'>> & {
    handleShortNameChange?: (shortName: string) => void;
    handleCountryCodeChange?: (countryCode: string | undefined) => void;
    handleCourseDaysChange: (courseDays: number) => void;
}) => {
    const citySearchText = useSelector(selectLocationListSearchText);
    const courseSearchText = useSelector(selectCourseListSearchText);
    const userSearchText = useSelector(selectUserListSearchText);
    const isLocationListLoading = useSelector(selectIsLocationListLoading);
    const isLocationListLoaded = useSelector(selectIsLocationListLoaded);
    const locationListError = useSelector(selectLocationListError);
    const isActivityStatusListLoading = useSelector(
        selectIsActivityStatusListLoading,
    );
    const isActivityStatusListLoaded = useSelector(
        selectIsActivityStatusListLoaded,
    );
    const isActivityTypeListLoading = useSelector(
        selectIsActivityTypeListLoading,
    );
    const isActivityTypeListLoaded = useSelector(
        selectIsActivityTypeListLoaded,
    );
    const activityTypeList = useSelector(selectAllActiveActivityTypes);
    const activityTypeListError = useSelector(selectActivityTypeListError);
    const isPartnerInitiativeListLoading = useSelector(
        selectIsPartnerInitiativeListLoading,
    );
    const isPartnerInitiativeListLoaded = useSelector(
        selectIsPartnerInitiativeListLoaded,
    );
    const partnerInitiativeListError = useSelector(
        selectPartnerInitiativeListError,
    );
    const isActivityModalityListLoading = useSelector(
        selectIsActivityModalityListLoading,
    );
    const isActivityModalityListLoaded = useSelector(
        selectIsActivityModalityListLoaded,
    );
    const activityModalityListError = useSelector(
        selectActivityModalityListError,
    );
    const isCourseListLoading = useSelector(selectIsCourseListLoading);
    const isCourseListLoaded = useSelector(selectIsCourseListLoaded);
    const courseListError = useSelector(selectCourseListError);
    const isDeliveryLanguageListLoading = useSelector(
        selectIsDeliveryLanguageLoading,
    );
    const isDeliveryLanguageListLoaded = useSelector(
        selectIsDeliveryLanguageLoaded,
    );
    const deliveryLanguageListError = useSelector(
        selectDeliveryLanguageListError,
    );
    const isUserListLoading = useSelector(selectIsUserListLoading);
    const isUserListLoaded = useSelector(selectIsUserListLoaded);
    const userListError = useSelector(selectUserListError);
    const originalLocationList = useSelector(selectAllActiveLocations);
    const preselectedCityId = getActivityCityString(formValues);
    const shouldCheckForValue = !!(
        formValues.delivery_city &&
        formValues.delivery_country &&
        formValues.delivery_geo &&
        formValues.delivery_region
    );
    const locationList = handlePreSelectedValue(
        {
            city: formValues.delivery_city as string,
            state: formValues.delivery_state as string,
            country: formValues.delivery_country,
            geo: formValues.delivery_geo,
            region: formValues.delivery_region,
            active: true,
            pk: `location-${Date.now()}`,
            city_timezone: formValues.delivery_timezone,
        },
        shouldCheckForValue && isLocationListLoaded,
        originalLocationList,
        (location) => getCityString(location) === preselectedCityId,
    );
    const activityModalityList = useSelector(selectAllActiveActivityModalities);
    const partnerInitiativeList = useSelector(
        selectAllActivePartnerInitiatives,
    );
    const courseList = useSelector(selectAllActiveCourses);
    const deliveryLanguageList = useSelector(selectAllActiveDeliveryLanguages);
    const userProfile = useSelector(selectUser);
    const originalUserList = useSelector(selectUserList);
    const userList = userProfile?.profile
        ? handlePreSelectedValue(
              userProfile.profile,
              !!formValues.operations_owner && isUserListLoaded,
              originalUserList,
              (user) => user.full_name === formValues.operations_owner,
          )
        : originalUserList;
    const [filteredCourseList, setFilteredCourseList] = useState(
        [] as Array<CourseItemData>,
    );
    const [programSelected, setProgramSelected] = useState(false);
    const [customers] = useState<Array<number>>(
        formValues.customers?.map((c, i) => i) ?? [],
    );
    const [invoices] = useState<Array<number>>(
        formValues.billing_invoices?.map((inv, i) => i) ?? [],
    );
    const dispatch = useDispatch();

    useEffect(() => {
        // initialize query params for business data slices
        // this code block should only run once
        dispatch(setLocationListRegion(userProfile?.profile.regions || []));
        dispatch(getLocationsList());

        return () => {
            // reset business data slices
            // this code block should only run once
            [
                resetLocationsSlice,
                resetActivityStatusesSlice,
                resetActivityTypesSlice,
                resetActivityAudiencesSlice,
                resetActivityModalitiesSlice,
                resetCoursesSlice,
                resetUserListSlice,
                resetInstructorRoleTypesSlice,
            ].forEach((resetFunction) => dispatch(resetFunction()));
        };
    }, [dispatch, userProfile]);

    // lifecycle method to fetch (and re- fetch) business data
    useEffect(() => {
        (
            [
                [
                    !isLocationListLoaded && !isLocationListLoading,
                    getLocationsList,
                ],
                [
                    !isActivityStatusListLoaded && !isActivityStatusListLoading,
                    getActivityStatusesList,
                ],
                [
                    !isActivityTypeListLoaded && !isActivityTypeListLoading,
                    getActivityTypesList,
                ],
                [
                    !isActivityAudienceListLoaded &&
                        !isActivityAudienceListLoading,
                    getActivityAudiencesList,
                ],
                [
                    !isPartnerInitiativeListLoaded &&
                        !isPartnerInitiativeListLoading,
                    getPartnerInitiativesList,
                ],
                [
                    !isActivityModalityListLoaded &&
                        !isActivityModalityListLoading,
                    getActivityModalitiesList,
                ],
                [!isCourseListLoaded && !isCourseListLoading, getCoursesList],
                [!isUserListLoaded && !isUserListLoading, getUserList],
                [
                    !isDeliveryLanguageListLoaded &&
                        !isDeliveryLanguageListLoading,
                    getDeliveryLanguagesList,
                ],
            ] as ReadonlyArray<[boolean, Function]>
        ).forEach(([shouldFetch, getList]) => {
            if (shouldFetch) {
                dispatch(getList());
            }
        });
    });

    const handleProgramChange = (programType: string) => {
        handleFieldEvent({
            program: programType,
        });
        let availableCourses: Array<CourseItemData> =
            [] as Array<CourseItemData>;
        if (programType) {
            availableCourses = courseList.filter((course: CourseItemData) =>
                course?.associated_programs?.includes(programType),
            );
        }
        setFilteredCourseList(availableCourses);
        handleFieldEvent({ course_name: '' });
        setProgramSelected(true);
    };

    useEffect(() => {
        if (handleInvoicesChange && handleCustomersChange) {
            handleInvoicesChange(invoices);
            handleCustomersChange(customers);
        }
    }, [invoices, customers, handleInvoicesChange, handleCustomersChange]);

    useEffect(() => {
        if (courseList) {
            let availableCourses: Array<CourseItemData> =
                [] as Array<CourseItemData>;
            if (formValues.program) {
                availableCourses = courseList.filter((course: CourseItemData) =>
                    course?.associated_programs?.includes(formValues.program),
                );
            }
            setFilteredCourseList(availableCourses);
            setProgramSelected(true);
        }
    }, [formValues.program, programSelected, courseList]);

    const [isTopicNameVisible, setIsTopicNameVisible] = useState(false);

    // Use centralized audience filtering hook
    const {
        filteredAudiences,
        isLoading: isActivityAudienceListLoading,
        isLoaded: isActivityAudienceListLoaded,
        error: activityAudienceListError,
    } = useFilteredAudiences(formValues.program);

    const {
        valueLookup: activityAudienceLookup,
        valueOptions: activityAudienceOptions,
    } = React.useMemo(() => {
        return getOptionsAndLookupForSelectInput<ActivityAudienceItemData>(
            filteredAudiences,
            (activityAudience: ActivityAudienceItemData) => ({
                label: activityAudience.activity_audience,
                id: activityAudience.pk as string,
            }),
        );
    }, [filteredAudiences]);

    // populate user profile programs as dropdown list
    const programList: Array<ProgramTypeItemData> = (
        userProfile?.profile.programs || []
    ).map(
        (val) =>
            ({
                pk: `${val}-${Date.now()}`,
                program_type: val,
                active: true,
            }) as ProgramTypeItemData,
    );

    const { valueLookup: programLookup, valueOptions: programOptions } =
        getOptionsAndLookupForSelectInput<ProgramTypeItemData>(
            programList,
            (program: ProgramTypeItemData) => ({
                label: program.program_type,
                id: program.pk as string,
            }),
        );

    const {
        valueLookup: activityModalityLookup,
        valueOptions: activityModalityOptions,
    } = getOptionsAndLookupForSelectInput<ActivityModalityItemData>(
        activityModalityList,
        (activityModality: ActivityModalityItemData) => ({
            label: activityModality.activity_modality,
            id: activityModality.pk as string,
        }),
    );

    const {
        valueLookup: activityTypeLookup,
        valueOptions: activityTypeOptions,
    } = getOptionsAndLookupForSelectInput<ActivityTypeItemData>(
        activityTypeList,
        (activityType: ActivityTypeItemData) => ({
            label: activityType.activity_type,
            id: activityType.pk as string,
        }),
    );

    const {
        valueLookup: partnerInitiativeLookup,
        valueOptions: partnerInitiativeOptions,
    } = getOptionsAndLookupForSelectInput<PartnerInitiativeItemData>(
        partnerInitiativeList,
        (partnerInitiative: PartnerInitiativeItemData) => ({
            label: partnerInitiative.partner_initiative,
            id: partnerInitiative.pk as string,
        }),
    );

    const { valueLookup: courseLookup, valueOptions: courseOptions } =
        getOptionsAndLookupForSelectInput<CourseItemData>(
            filteredCourseList,
            (course: CourseItemData) => ({
                label: course.course,
                id: course.pk as string,
            }),
        );

    const { valueLookup: userLookup, valueOptions: userOptions } =
        getOptionsAndLookupForSelectInput<
            Partial<UserProfileData> &
                Pick<UserProfileData, 'email' | 'full_name'>
        >(
            [
                ...userList,
                {
                    email: formValues.operations_owner_email,
                    full_name: formValues.operations_owner,
                },
            ],
            (
                profile: Partial<UserProfileData> &
                    Pick<UserProfileData, 'email' | 'full_name'>,
            ) => ({
                label: profile.full_name,
                id: profile.email as string,
            }),
        );

    const { citiesOptions, citiesOptionLookup, citiesEntityLookup } =
        locationList.reduce(
            (acc, city) => {
                const option = {
                    label: getCityString(city),
                    id: getCityString(city),
                };
                acc.citiesEntityLookup[option.id] = city;
                acc.citiesOptionLookup[option.id] = option;

                const shouldAddOptionToList = !(
                    citySearchText && option.id === preselectedCityId
                );

                if (shouldAddOptionToList) {
                    acc.citiesOptions.push(option);
                }

                return acc;
            },
            {
                citiesOptions: [] as Array<Select.Option>,
                citiesOptionLookup: {} as {
                    [key: string]: Select.Option;
                },
                citiesEntityLookup: {} as {
                    [key: string]: LocationItemData;
                },
            },
        );

    const handleLocationDelayedFilteringChange = (
        e: CustomDetailEvent<Select.DelayedFilteringChangeDetail>,
    ) => {
        if (e.detail.value !== citySearchText) {
            dispatch(setLocationListSearchText(e.detail.value));
            dispatch(getLocationsList());
        }
    };

    const handleLocationChange = (
        e: CustomDetailEvent<Select.ChangeDetail>,
    ) => {
        if (e.detail.selectedOption) {
            dispatch(setLocationListSearchText(null));
            const cityLookupRecord =
                citiesEntityLookup[e.detail.selectedOption.id];
            const {
                city: delivery_city,
                state: delivery_state,
                country: delivery_country,
                geo: delivery_geo,
                region: delivery_region,
                city_timezone: delivery_timezone,
            } = cityLookupRecord;
            if (handleCountryCodeChange) {
                handleCountryCodeChange(cityLookupRecord.country_code);
            }
            handleFieldEvent({
                delivery_city,
                delivery_state,
                delivery_country,
                delivery_geo,
                delivery_region,
                ...(delivery_timezone ? { delivery_timezone } : {}),
            });
        }
    };

    const handleOperationsOwnerDelayedFilteringChange = (
        e: CustomDetailEvent<Select.DelayedFilteringChangeDetail>,
    ) => {
        dispatch(setUserListSearchText(e.detail.value));
        dispatch(getUserList());
    };

    const handleOperationsOwnerChange = (
        e: CustomDetailEvent<Select.ChangeDetail>,
    ) => {
        if (e.detail.selectedOption) {
            dispatch(setUserListSearchText(null));
            handleFieldEvent({
                operations_owner: e.detail.selectedOption.label,
                operations_owner_email: e.detail.selectedOption.id,
            });
        }
    };

    const handleSchedulerChange = (
        e: CustomDetailEvent<Select.ChangeDetail>,
    ) => {
        if (e.detail.selectedOption) {
            dispatch(setUserListSearchText(null));
            handleFieldEvent({
                scheduler: e.detail.selectedOption.label,
                scheduler_email: e.detail.selectedOption.id,
            });
        }
    };

    const handleCustomerSuccessManagerChange = (
        e: CustomDetailEvent<Select.ChangeDetail>,
    ) => {
        if (e.detail.selectedOption) {
            dispatch(setUserListSearchText(null));
            handleFieldEvent({
                customer_support_manager: e.detail.selectedOption.id
                    ? e.detail.selectedOption.label
                    : null,
                customer_support_manager_email: e.detail.selectedOption.id,
            });
        }
    };

    const getCustomerSuccessManagerSelectedOption = () => {
        return userLookup.hasOwnProperty(formValues.customer_support_manager)
            ? userLookup[formValues.customer_support_manager]
            : {
                  id: formValues.customer_support_manager_email,
                  label: formValues.customer_support_manager,
              };
    };

    const getSchedulerSelectedOption = () => {
        return userLookup.hasOwnProperty(formValues.scheduler)
            ? userLookup[formValues.scheduler]
            : { id: formValues.scheduler_email, label: formValues.scheduler };
    };

    const getOperationsOwnerSelectedOption = () => {
        return userLookup.hasOwnProperty(formValues.operations_owner)
            ? userLookup[formValues.operations_owner]
            : {
                  id: formValues.operations_owner_email,
                  label: formValues.operations_owner,
              };
    };

    const handleCustomerSuccessManagerDelayedFilteringChange = (
        e: CustomDetailEvent<Select.DelayedFilteringChangeDetail>,
    ) => {
        dispatch(setUserListSearchText(e.detail.value));
        dispatch(getUserList());
    };

    const handleCourseDelayedFilteringChange = (
        e: CustomDetailEvent<Select.DelayedFilteringChangeDetail>,
    ) => {
        if (e.detail.value !== courseSearchText) {
            dispatch(setCourseListSearchText(e.detail.value));
            dispatch(getCoursesList());
        }
    };

    const handleCourseChange = (e: CustomDetailEvent<Select.ChangeDetail>) => {
        if (e.detail.selectedOption) {
            dispatch(setCourseListSearchText(null));

            // change sessions if there is a start day already selected
            if (
                sessionAttributeEditorItems.length &&
                sessionAttributeEditorItems[0].dateString
            ) {
                const sessions = getDefaultSessionsForCourse(
                    sessionAttributeEditorItems[0].dateString,
                    e.detail.selectedOption.label,
                    courseList,
                );
                handleSessionItemEvent(sessions);
            }

            const courseName = e.detail.selectedOption.label;

            const courseItem = courseList.find(
                (course: CourseItemData) => course.course === courseName,
            );
            handleFieldEvent({
                course_name: courseName,
                course_catalog_item_id: courseItem?.tc3_catalog_item_id || null,
                catalog_item_versioned_id:
                    courseItem?.catalog_item_versioned_id || null,
            });
            setIsTopicNameVisible(courseItem?.topic_name_required);
            const courseShortName = courseItem?.short_name as string;
            let courseDays = courseItem?.course_days as string;
            if (courseDays && courseDays !== '') {
                handleCourseDaysChange(parseFloat(courseDays));
            }
            if (handleShortNameChange) {
                handleShortNameChange(
                    courseShortName || courseName.substring(0, 10),
                );
            }
        }
    };

    const handleSessionDateChange = (
        val: string,
        sessionItem: SessionAttributeEditorItem,
        index: number,
    ) => {
        if (index > 0) {
            const newItems = sessionAttributeEditorItems.slice();
            newItems.splice(index, 1, {
                ...sessionItem,
                dateString: val,
            });

            handleSessionItemEvent(newItems);
        } else {
            handleSessionItemEvent(
                getDefaultSessionsForCourse(
                    val,
                    formValues.course_name,
                    courseList,
                ),
            );
        }
    };

    const sessionAttributeEditorDefinition: Array<AttributeEditor.FieldDefinition> =
        [
            {
                label: 'Session dates',
                control: (
                    sessionItem: SessionAttributeEditorItem,
                    index: number,
                ) => (
                    <DatePicker
                        placeholder="YYYY/MM/DD"
                        todayLabel="Today"
                        nextMonthLabel="Next month"
                        previousMonthLabel="Previous month"
                        value={
                            sessionItem.dateString ? sessionItem.dateString : ''
                        }
                        onChange={(e) => {
                            handleSessionDateChange(
                                e.detail.value,
                                sessionItem,
                                index,
                            );
                        }}
                        data-testid={`${mode}ActivityDate${index}`}
                    />
                ),
                errorText: (
                    sessionItem: SessionAttributeEditorItem,
                    index: number,
                ) =>
                    controlArrayErrors.sessionAttributeEditorItems?.[index]
                        ?.dateString,
            },
            {
                label: 'Start time',
                control: (
                    sessionItem: SessionAttributeEditorItem,
                    index: number,
                ) => (
                    <div className="time-selector">
                        <div>
                            <TimeInput
                                data-testid={`${mode}ActivityStartTime${index}`}
                                format="hh:mm"
                                placeholder="hh:mm"
                                use24Hour={false}
                                value={
                                    sessionItem.startTime
                                        ? sessionItem.startTime
                                        : ''
                                }
                                onChange={(e) => {
                                    const newItems =
                                        sessionAttributeEditorItems.slice();
                                    newItems.splice(index, 1, {
                                        ...sessionItem,
                                        startTime: e.detail.value,
                                    });
                                    handleSessionItemEvent(newItems);
                                }}
                            />
                        </div>
                        <div>
                            <Select
                                options={meridiemFieldOptions}
                                selectedOption={
                                    sessionItem.startTimeMeridiemFieldOption
                                        ? meridiemFieldOptionLookup[
                                              sessionItem
                                                  .startTimeMeridiemFieldOption
                                          ]
                                        : meridiemFieldOptionLookup[
                                              MeridiemFieldValue.Am
                                          ]
                                }
                                onChange={(e) => {
                                    const newItems =
                                        sessionAttributeEditorItems.slice();
                                    newItems.splice(index, 1, {
                                        ...sessionItem,
                                        startTimeMeridiemFieldOption: e.detail
                                            .selectedOption
                                            .label as MeridiemFieldValue,
                                    });
                                    handleSessionItemEvent(newItems);
                                }}
                                data-testid={`${mode}ActivityEnd${index}`}
                            />
                        </div>
                    </div>
                ),
                errorText: (
                    sessionItem: SessionAttributeEditorItem,
                    index: number,
                ) =>
                    controlArrayErrors.sessionAttributeEditorItems?.[index]
                        ?.startTime ||
                    controlArrayErrors.sessionAttributeEditorItems?.[index]
                        ?.startTimeMeridiemFieldOption,
            },
            {
                label: 'End time',
                control: (
                    sessionItem: SessionAttributeEditorItem,
                    index: number,
                ) => (
                    <div className="time-selector">
                        <div>
                            <TimeInput
                                data-testid={`${mode}ActivityEndTime${index}`}
                                format="hh:mm"
                                placeholder="hh:mm"
                                use24Hour={false}
                                value={
                                    sessionItem.endTime
                                        ? sessionItem.endTime
                                        : ''
                                }
                                onChange={(e) => {
                                    const newItems =
                                        sessionAttributeEditorItems.slice();
                                    newItems.splice(index, 1, {
                                        ...sessionItem,
                                        endTime: e.detail.value,
                                    });
                                    handleSessionItemEvent(newItems);
                                }}
                            />
                        </div>
                        <div>
                            <Select
                                options={meridiemFieldOptions}
                                selectedOption={
                                    sessionItem.endTimeMeridiemFieldOption
                                        ? meridiemFieldOptionLookup[
                                              sessionItem
                                                  .endTimeMeridiemFieldOption
                                          ]
                                        : meridiemFieldOptionLookup[
                                              MeridiemFieldValue.Am
                                          ]
                                }
                                onChange={(e) => {
                                    const newItems =
                                        sessionAttributeEditorItems.slice();
                                    newItems.splice(index, 1, {
                                        ...sessionItem,
                                        endTimeMeridiemFieldOption: e.detail
                                            .selectedOption
                                            .label as MeridiemFieldValue,
                                    });
                                    handleSessionItemEvent(newItems);
                                }}
                                data-testid={`${mode}ActivityEnd${index}`}
                            />
                        </div>
                    </div>
                ),
                errorText: (
                    sessionItem: SessionAttributeEditorItem,
                    index: number,
                ) =>
                    controlArrayErrors.sessionAttributeEditorItems?.[index]
                        ?.endTime ||
                    controlArrayErrors.sessionAttributeEditorItems?.[index]
                        ?.endTimeMeridiemFieldOption,
            },
        ];

    const {
        valueLookup: deliveryLanguageLookup,
        valueOptions: deliveryLanguageOptions,
    } = getOptionsAndLookupForSelectInput<DeliveryLanguageItemData>(
        deliveryLanguageList,
        (deliveryLanguage: DeliveryLanguageItemData) => ({
            label: deliveryLanguage.delivery_language,
            id: deliveryLanguage.pk as string,
        }),
    );

    const doSetShortCourseName = useCallback(() => {
        const courseItem = courseList.find(
            (course: CourseItemData) =>
                course.course === formValues.course_name,
        );
        const courseShortName = courseItem?.short_name as string;
        let courseDays = courseItem?.course_days as string;
        if (courseDays && courseDays !== '') {
            handleCourseDaysChange(parseFloat(courseDays));
        }
        if (handleShortNameChange && formValues.course_name) {
            handleShortNameChange(
                courseShortName || formValues.course_name.substring(0, 10),
            );
        }
    }, [
        courseList,
        formValues.course_name,
        handleCourseDaysChange,
        handleShortNameChange,
    ]);

    useEffect(() => {
        if (isCourseListLoaded) {
            doSetShortCourseName();
        }
    }, [isCourseListLoaded, doSetShortCourseName]);

    return (
        <SpaceBetween direction="vertical" size="m">
            <h3>Activity details</h3>
            <ColumnLayout>
                <div data-awsui-column-layout-root="true">
                    <SelectActivityType
                        onChange={(newType) => {
                            const updates: any = {
                                activity_type: newType,
                            };
                            // Clear myClass LMS if switching to Webinar
                            // myClass is not supported for Webinar activities (CR-260952004)
                            if (
                                newType === 'Webinar' &&
                                (formValues as any).lms_type === 'myClass'
                            ) {
                                updates.lms_type = '';
                            }
                            handleFieldEvent(updates);
                        }}
                        options={activityTypeOptions}
                        selectedOption={
                            activityTypeLookup[formValues.activity_type]
                        }
                        isLoading={isActivityTypeListLoading}
                        error={errors?.activity_type}
                        onRecoveryClick={() => {
                            dispatch(resetActivityTypesSlice());
                            dispatch(getActivityTypesList());
                        }}
                        statusType={getStatusType(
                            isActivityTypeListLoading,
                            isActivityTypeListLoaded,
                            activityTypeListError,
                        )}
                        data-testid={'OpportunityActivityType'}
                    />
                    {programOptions.length === 0 && (
                        <FormField label="Program">
                            {formValues.program}
                        </FormField>
                    )}

                    {programOptions.length > 0 && (
                        <FormField errorText={errors?.program} label="Program">
                            <Select
                                className={
                                    errors?.program && FORM_ERROR_SELECTOR
                                }
                                placeholder={
                                    mode === FormSectionMode.Edit
                                        ? 'Loading programs'
                                        : 'Select a program'
                                }
                                options={programOptions}
                                selectedOption={
                                    programLookup[formValues.program]
                                }
                                onChange={(e) =>
                                    handleProgramChange(
                                        e.detail.selectedOption.label,
                                    )
                                }
                                loadingText="Loading programs"
                                errorText="An error occurred while loading programs"
                                recoveryText="Retry"
                                empty="No programs found"
                                data-testid={`${mode}ActivityProgram`}
                            />
                        </FormField>
                    )}
                    <FormField
                        errorText={errors?.activity_audience}
                        label="Audience"
                    >
                        <Select
                            className={
                                errors?.activity_audience && FORM_ERROR_SELECTOR
                            }
                            placeholder={
                                mode === FormSectionMode.Edit &&
                                isActivityAudienceListLoading
                                    ? 'Loading audiences'
                                    : 'Select an audience'
                            }
                            options={activityAudienceOptions}
                            selectedOption={
                                activityAudienceLookup[
                                    formValues.activity_audience
                                ]
                            }
                            onChange={(e) =>
                                handleFieldEvent({
                                    activity_audience:
                                        e.detail.selectedOption.label,
                                })
                            }
                            onRecoveryClick={() => {
                                dispatch(resetActivityAudiencesSlice());
                                dispatch(getActivityAudiencesList());
                            }}
                            loadingText="Loading audiences"
                            errorText="An error occurred while loading audiences"
                            recoveryText="Retry"
                            empty="No audiences found"
                            statusType={getStatusType(
                                isActivityAudienceListLoading,
                                isActivityAudienceListLoaded,
                                activityAudienceListError,
                            )}
                            data-testid={`${mode}ActivityAudience`}
                        />
                    </FormField>
                    <FormField
                        errorText={errors?.activity_modality}
                        label="Modality"
                    >
                        <Select
                            className={
                                errors?.activity_modality && FORM_ERROR_SELECTOR
                            }
                            placeholder={
                                mode === FormSectionMode.Edit &&
                                isActivityModalityListLoading
                                    ? 'Loading modalities'
                                    : 'Select a modality'
                            }
                            options={activityModalityOptions}
                            selectedOption={
                                activityModalityLookup[
                                    formValues.activity_modality
                                ]
                            }
                            onChange={(e) =>
                                handleFieldEvent({
                                    activity_modality:
                                        e.detail.selectedOption.label,
                                })
                            }
                            onRecoveryClick={() => {
                                dispatch(resetActivityModalitiesSlice());
                                dispatch(getActivityModalitiesList());
                            }}
                            loadingText="Loading modalities"
                            errorText="An error occurred while loading modalities"
                            recoveryText="Retry"
                            empty="No modalities found"
                            statusType={getStatusType(
                                isActivityModalityListLoading,
                                isActivityModalityListLoaded,
                                activityModalityListError,
                            )}
                            data-testid={`${mode}ActivityModality`}
                        />
                    </FormField>
                    <FormField
                        errorText={errors?.course_name}
                        label="Course"
                        description={`"${formValues.sfdc_course_name}" imported from SFDC.`}
                    >
                        <Select
                            className={
                                errors?.course_name && FORM_ERROR_SELECTOR
                            }
                            placeholder={
                                mode === FormSectionMode.Edit &&
                                isCourseListLoading
                                    ? 'Loading courses'
                                    : 'Select a course'
                            }
                            disabled={!programSelected}
                            options={courseOptions}
                            selectedOption={
                                formValues.course_name
                                    ? courseLookup[formValues.course_name]
                                    : null
                            }
                            onChange={handleCourseChange}
                            onRecoveryClick={() => {
                                dispatch(setCourseListSearchText(''));
                                dispatch(resetCoursesSlice());
                                dispatch(getCoursesList());
                            }}
                            onDelayedFilteringChange={
                                handleCourseDelayedFilteringChange
                            }
                            loadingText="Loading courses"
                            errorText="An error occurred while loading courses"
                            recoveryText="Retry"
                            filteringType="manual"
                            filteringPlaceholder="Enter a course name"
                            empty="No courses found"
                            statusType={getStatusType(
                                isCourseListLoading,
                                isCourseListLoaded,
                                courseListError,
                            )}
                            data-testid={`${mode}ActivityCourse`}
                        />
                        <span className="awsui-form-field-description">{`${sessionAttributeEditorItems.length} course days`}</span>
                    </FormField>
                    {isTopicNameVisible && (
                        <FormField
                            errorText={errors?.topic_name}
                            label={<span>Topic Name</span>}
                        >
                            <Input
                                className={
                                    errors?.topic_name && FORM_ERROR_SELECTOR
                                }
                                value={formValues.topic_name}
                                placeholder="Enter a description for the course topic"
                                onInput={(e) =>
                                    handleFieldEvent({
                                        topic_name: e.detail.value,
                                    })
                                }
                                data-testid={`EditTopicName`}
                            />
                        </FormField>
                    )}
                    <FormField
                        label="Operations owner"
                        errorText={errors?.operations_owner}
                    >
                        <Select
                            className={
                                errors?.operations_owner && FORM_ERROR_SELECTOR
                            }
                            placeholder={
                                mode === FormSectionMode.Edit &&
                                isUserListLoading
                                    ? 'Loading users'
                                    : 'Select user'
                            }
                            empty="No users found"
                            loadingText="Loading users"
                            filteringType="manual"
                            errorText="An error occurred while loading users"
                            recoveryText="Retry"
                            statusType={getStatusType(
                                isUserListLoading,
                                isUserListLoaded,
                                userListError,
                            )}
                            options={userOptions}
                            selectedOption={getOperationsOwnerSelectedOption()}
                            filteringPlaceholder="Enter a user name"
                            onDelayedFilteringChange={
                                handleOperationsOwnerDelayedFilteringChange
                            }
                            onChange={handleOperationsOwnerChange}
                            onRecoveryClick={() => {
                                dispatch(setUserListSearchText(''));
                                dispatch(resetUserListSlice());
                                dispatch(getUserList());
                            }}
                            data-testid={`${mode}ActivityOperationsOwner`}
                            disabled={
                                mode === FormSectionMode.Edit &&
                                !isUserListLoaded
                            }
                        />
                    </FormField>
                    <FormField label="Scheduler" errorText={errors?.scheduler}>
                        <Select
                            className={errors?.scheduler && FORM_ERROR_SELECTOR}
                            placeholder={
                                mode === FormSectionMode.Edit &&
                                isUserListLoading
                                    ? 'Loading users'
                                    : 'Select user'
                            }
                            empty="No users found"
                            loadingText="Loading users"
                            filteringType="manual"
                            errorText="An error occurred while loading users"
                            recoveryText="Retry"
                            statusType={getStatusType(
                                isUserListLoading,
                                isUserListLoaded,
                                userListError,
                            )}
                            options={userOptions}
                            selectedOption={getSchedulerSelectedOption()}
                            filteringPlaceholder="Enter a user name"
                            onDelayedFilteringChange={
                                handleOperationsOwnerDelayedFilteringChange
                            }
                            onChange={handleSchedulerChange}
                            onRecoveryClick={() => {
                                dispatch(setUserListSearchText(''));
                                dispatch(resetUserListSlice());
                                dispatch(getUserList());
                            }}
                            data-testid={`${mode}ActivityScheduler`}
                            disabled={
                                mode === FormSectionMode.Edit &&
                                !isUserListLoaded
                            }
                        />
                    </FormField>
                    <EmailTokenInput
                        value={formValues.customer_support_manager_email || ''}
                        errorText={errors?.customer_support_manager}
                        onChange={(value) =>
                            handleFieldEvent({
                                customer_support_manager_email: value,
                                customer_support_manager: value,
                            })
                        }
                        testId={`${mode}ActivityCustomerSuccessManager`}
                        label={
                            <span>
                                CSM/BDM/CEM <i>- optional</i>
                            </span>
                        }
                        description="People added to this field will be added to customer email notification in CC'd"
                    />
                    <FormField
                        label="Delivery location"
                        errorText={
                            errors?.delivery_city ||
                            errors?.delivery_region ||
                            errors?.delivery_geo ||
                            errors?.delivery_country
                        }
                        description={`"${formValues.sfdc_location}" imported from SFDC.`}
                    >
                        <Select
                            className={
                                (errors?.delivery_city ||
                                    errors?.delivery_region ||
                                    errors?.delivery_geo ||
                                    errors?.delivery_country) &&
                                FORM_ERROR_SELECTOR
                            }
                            placeholder={
                                mode === FormSectionMode.Edit &&
                                isLocationListLoading
                                    ? LocationFormFieldText.Loading
                                    : LocationFormFieldText.Placeholder
                            }
                            empty={LocationFormFieldText.Empty}
                            loadingText={LocationFormFieldText.Loading}
                            filteringType="manual"
                            errorText={LocationFormFieldText.Error}
                            recoveryText={LocationFormFieldText.Recovery}
                            statusType={getStatusType(
                                isLocationListLoading,
                                isLocationListLoaded,
                                locationListError,
                            )}
                            options={citiesOptions}
                            selectedOption={(() => {
                                const cityString =
                                    getActivityCityString(formValues);
                                return cityString
                                    ? citiesOptionLookup[cityString]
                                    : null;
                            })()}
                            filteringPlaceholder={
                                LocationFormFieldText.FilteringPlaceholder
                            }
                            onDelayedFilteringChange={
                                handleLocationDelayedFilteringChange
                            }
                            onChange={handleLocationChange}
                            onRecoveryClick={() => {
                                dispatch(setLocationListSearchText(''));
                                dispatch(resetPartialLocationsSlice());
                                dispatch(getLocationsList());
                            }}
                            data-testid={`${mode}ActivityCity`}
                            disabled={
                                mode === FormSectionMode.Edit &&
                                !isLocationListLoaded
                            }
                        />
                    </FormField>
                    <FormField
                        label="Partner initiative"
                        errorText={errors?.partner_initiative}
                    >
                        <Select
                            className={
                                errors?.partner_initiative &&
                                FORM_ERROR_SELECTOR
                            }
                            placeholder={
                                isDeliveryLanguageListLoading
                                    ? 'Loading partner initiative'
                                    : 'Select a partner initiative'
                            }
                            options={partnerInitiativeOptions}
                            selectedOption={
                                partnerInitiativeLookup[
                                    formValues.partner_initiative
                                ]
                            }
                            onChange={(e) => {
                                handleFieldEvent({
                                    partner_initiative:
                                        e.detail.selectedOption.label,
                                });
                            }}
                            onRecoveryClick={() => {
                                dispatch(resetPartnerInitiativesSlice());
                                dispatch(getPartnerInitiativesList());
                            }}
                            loadingText="Loading partner initiatives"
                            errorText="An error occurred while loading partner initiatives"
                            recoveryText="Retry"
                            empty="No partner initiatives found"
                            statusType={getStatusType(
                                isPartnerInitiativeListLoading,
                                isPartnerInitiativeListLoaded,
                                partnerInitiativeListError,
                            )}
                            data-testid={`${mode}PartnerInitiative`}
                            disabled={
                                mode === FormSectionMode.Create &&
                                !isPartnerInitiativeListLoaded
                            }
                        />
                    </FormField>
                    <FormField
                        label="Delivery language"
                        errorText={errors?.delivery_language}
                        description={`"${formValues.sfdc_language}" imported from SFDC.`}
                    >
                        <Select
                            className={
                                errors?.delivery_language && FORM_ERROR_SELECTOR
                            }
                            placeholder={
                                isDeliveryLanguageListLoading
                                    ? 'Loading delivery languages'
                                    : 'Select a delivery language'
                            }
                            options={deliveryLanguageOptions}
                            selectedOption={
                                deliveryLanguageLookup[
                                    formValues.delivery_language
                                ]
                            }
                            onChange={(e) => {
                                handleFieldEvent({
                                    delivery_language:
                                        e.detail.selectedOption.label,
                                });
                            }}
                            onRecoveryClick={() => {
                                dispatch(resetDeliveryLanguagesSlice());
                                dispatch(getDeliveryLanguagesList());
                            }}
                            loadingText="Loading delivery Languages"
                            errorText="An error occurred while loading delivery languages"
                            recoveryText="Retry"
                            empty="No delivery languages found"
                            statusType={getStatusType(
                                isDeliveryLanguageListLoading,
                                isDeliveryLanguageListLoaded,
                                deliveryLanguageListError,
                            )}
                            data-testid={`${mode}DeliveryLanguage`}
                            disabled={
                                mode === FormSectionMode.Create &&
                                !isDeliveryLanguageListLoaded
                            }
                        />
                    </FormField>
                    <FormField
                        label={
                            <div className="awsui-util-status-inactive">
                                Timezone
                            </div>
                        }
                    >
                        {formValues.delivery_timezone}
                    </FormField>
                    <FormField>
                        <AttributeEditor
                            data-testid="SessionAttributeEditor"
                            empty="No session dates have been added"
                            addButtonText="Add date"
                            removeButtonText="Remove"
                            items={sessionAttributeEditorItems}
                            definition={sessionAttributeEditorDefinition}
                            onAddButtonClick={() =>
                                handleSessionItemEvent([
                                    ...sessionAttributeEditorItems,
                                    createEmptySessionAttributeEditorItem(),
                                ])
                            }
                            onRemoveButtonClick={({
                                detail: { itemIndex },
                            }) => {
                                const newList =
                                    sessionAttributeEditorItems.slice();
                                newList.splice(itemIndex, 1);
                                handleSessionItemEvent(newList);
                            }}
                            isItemRemovable={() =>
                                sessionAttributeEditorItems.length > 1
                            }
                        />
                    </FormField>
                </div>
            </ColumnLayout>
        </SpaceBetween>
    );
};

OpporotunityDetailsFormSection.propTypes = {
    sessionAttributeEditorItems: PropTypes.array.isRequired,
};

export default OpporotunityDetailsFormSection;
